<template>
  <div class="min-h-screen bg-gray-950">
    <SiteHeaderRibbon sticky />
    <SiteNavBar :sticky="false" />

    <PageContainer as="main" class="py-16 lg:py-24">
      <Breadcrumbs :items="breadcrumbs" class="mb-8" @navigate="trackBreadcrumb" />

      <BaseHeading as="h1" size="display" class="max-w-4xl">
        Brand colors of the
        <GradientText>world's top websites</GradientText>
      </BaseHeading>
      <p class="mt-6 max-w-2xl text-lg leading-8 text-gray-300">
        Official brand colors, hex codes and RGB values for {{ brands.length }} popular sites, extracted from their
        logos, stylesheets and live homepages. Copy any color, or generate a fresh palette from any URL.
      </p>

      <ul role="list" class="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <BrandCard v-for="brand in brands" :key="brand.slug" :brand="brand" />
      </ul>

      <!-- Same API panel the generator closes with. -->
      <div class="mt-16">
        <ApiAccessCard heading-tag="h2" />
      </div>
    </PageContainer>

    <!-- The search sits above the footer, so keep the page scrollable past it. -->
    <SiteFooter :credit="false" class="pb-32 sm:pb-28" />

    <BrandSearchBar :brands="brands" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import GradientText from '~/components/atoms/GradientText.vue'
import PageContainer from '~/components/atoms/PageContainer.vue'
import Breadcrumbs from '~/components/molecules/Breadcrumbs.vue'
import SiteHeaderRibbon from '~/components/organisms/SiteHeaderRibbon.vue'
import SiteNavBar from '~/components/organisms/SiteNavBar.vue'
import SiteFooter from '~/components/organisms/SiteFooter.vue'
import BrandSearchBar from '~/components/organisms/BrandSearchBar.vue'
import BrandCard from '~/components/molecules/BrandCard.vue'
import ApiAccessCard from '~/components/molecules/ApiAccessCard.vue'
import { SITE_URL } from '~/utils/site'

// Resolved at prerender time (see nitro.prerender.routes), so the listing ships
// inside the static HTML — no client-side request, nothing for a crawler to wait
// on. `default` keeps the template safe if the payload is ever missing.
const { data } = await useAsyncData('brand-catalog', () => $fetch('/api/brands'), {
  default: () => [],
})
const brands = computed(() => data.value ?? [])

const breadcrumbs = [{ label: 'Colorize', to: '/' }, { label: 'Brand colors' }]

const trackBreadcrumb = () => {
  try {
    trackEvent('Brand Hub Breadcrumb (generator)')
  } catch {}
}

const canonical = `${SITE_URL}/brand-colors`
const title = 'Brand Colors of Popular Websites - Hex Codes & Palettes'
const description =
  'Browse the official brand colors, hex codes and RGB values of the most popular websites, extracted from their logos, CSS and homepages. Free to copy, no sign-up.'

// The catalog is resolved before this runs (the useAsyncData above is awaited),
// so the JSON-LD is a plain string baked into the prerendered document.
const jsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${canonical}#webpage`,
  url: canonical,
  name: title,
  description,
  isPartOf: { '@type': 'WebSite', name: 'Colorize', url: SITE_URL },
  mainEntity: {
    '@type': 'ItemList',
    numberOfItems: brands.value.length,
    itemListElement: brands.value.map((brand, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${SITE_URL}/brand-colors/${brand.slug}`,
      name: `${brand.name} brand colors`,
    })),
  },
  // `<` can't appear raw inside a <script> block without ending it early.
}).replace(/</g, '\\u003c')

useHead({
  title,
  link: [
    { rel: 'canonical', href: canonical },
    // Brand logos are served from the Prefetch CDN; open the connection early.
    { rel: 'preconnect', href: 'https://curated.prefetch.io', crossorigin: '' },
  ],
  htmlAttrs: { lang: 'en' },
  script: [{ type: 'application/ld+json', innerHTML: jsonLd }],
})

useSeoMeta({
  description,
  ogTitle: title,
  ogDescription: description,
  ogType: 'website',
  ogUrl: canonical,
  ogImage: `${SITE_URL}/og-image.jpg`,
  twitterCard: 'summary_large_image',
})
</script>
